package com.trevorwiebe.timesheet.admin.employeeHours.domain

import com.trevorwiebe.timesheet.admin.reporting.presentation.CsvFile
import com.trevorwiebe.timesheet.core.domain.model.Rate

/**
 * Builds the "previous pay period" payroll CSV, matching the legacy admin export
 * (`timesheet-admin/build/js/utils/csv-utils.mjs`): one row per employee + rate, no header,
 * columns `payEntryId,employeeName,rateCode,hours`. Employees with no previous-period hours
 * produce no rows. Rows are the per-rate totals shown on the report, including the weekly
 * overtime buckets ("<description> OT Hours") produced by [usecases.AddUpHoursWithOvertime].
 *
 * Regular rows use each rate's stored [Rate.rateCode]; overtime rows use [Rate.overtimeRateCode],
 * falling back to "<rateCode>OT" when the admin hasn't set one so OT rows stay distinguishable.
 * A rate that somehow has no code passes its bare description through.
 */
fun List<EmployeeHoursSummary>.toPreviousPayPeriodCsv(rates: List<Rate>): CsvFile {
    val rateByDescription = rates.associateBy { it.description }

    val range = firstNotNullOfOrNull { it.previousTimeSheet }
        ?.let { "_${it.payPeriodStart.date}_to_${it.payPeriodEnd.date}" }
        ?: ""

    val sb = StringBuilder()
    forEach { summary ->
        summary.previous.hoursByRate.forEach { (rateLabel, hours) ->
            // Labels are "<description> Hours" or "<description> OT Hours"; the bare
            // description keys the payroll-code lookup.
            val withoutHours = rateLabel.removeSuffix(" Hours")
            val isOvertime = withoutHours.endsWith(" OT")
            val description = withoutHours.removeSuffix(" OT")
            val rate = rateByDescription[description]
            val code = when {
                rate == null -> withoutHours
                isOvertime -> rate.overtimeRateCode.ifBlank { "${rate.rateCode}OT" }
                else -> rate.rateCode.ifBlank { description }
            }
            sb.append(summary.employee.payEntryId.csvField())
                .append(',')
                .append(summary.employee.name.csvField())
                .append(',')
                .append(code)
                .append(',')
                .append(hours.hoursString())
                .append('\n')
        }
    }
    return CsvFile(fileName = "employee-hours$range.csv", content = sb.toString())
}

/** Drops a trailing `.0` so whole hours print like the legacy JS numbers (`80`, not `80.0`). */
private fun Double.hoursString(): String =
    if (this == toLong().toDouble()) toLong().toString() else toString()

/** Quotes and escapes a field if it contains a comma, quote, or newline. */
private fun String.csvField(): String =
    if (any { it == ',' || it == '"' || it == '\n' || it == '\r' }) {
        "\"${replace("\"", "\"\"")}\""
    } else {
        this
    }
